import { FileDownload } from '@mui/icons-material';
import { Box, CircularProgress } from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- client-side report, no server-side pagination
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { fetchDisabledRelayLoadReport } from '@sophocles/api/reports';
import DataGridToolbar, { ToolbarIconButton } from '@sophocles/data-grids/components/toolbar';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';

import { exportReportToExcel } from '../utils/reportExport';

function commStatusFromOnline(isOnline) {
  if (isOnline === null || isOnline === undefined) return t('reports.noRadio', 'No Radio');
  return isOnline ? t('general.online') : t('general.offline');
}

function DisabledPanelsReportGrid({ baseStructureLink, isBillboard, siteGroupId, siteGroup }) {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const { singular: relayLoadTerm, plural: relayLoadTermPlural } = useRelayLoadTerm(
    isBillboard
      ? { singularKey: 'reports.panel', pluralKey: 'reports.panels' }
      : { singularKey: 'reports.zone', pluralKey: 'reports.zones' },
  );

  useEffect(() => {
    if (!siteGroupId) return;
    setLoading(true);
    fetchDisabledRelayLoadReport(siteGroupId)
      .then(data => {
        const next = (data || [])
          .map((row, idx) => ({
            id: row.id ?? idx,
            name: row.name ?? '',
            description: row.description ?? '',
            site_id: row.site_id ?? 0,
            site_name: row.site_name ?? '',
            site_address: row.site_address ?? '',
            comm: commStatusFromOnline(row.is_online ?? null),
            maintenance: row.maintenance ?? '',
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setRows(next);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [siteGroupId]);

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: `${relayLoadTerm} ${t('general.name')}`,
        flex: 1,
        minWidth: 160,
      },
      {
        field: 'description',
        headerName: `${relayLoadTerm} ${t('general.description')}`,
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'site_name',
        headerName: isBillboard
          ? t('reports.structure', { defaultValue: 'Structure' })
          : t('reports.site', { defaultValue: 'Site' }),
        flex: 1,
        minWidth: 160,
        renderCell: params => <Link to={`${baseStructureLink}${params.row.site_id}`}>{params.value}</Link>,
      },
      {
        field: 'site_address',
        headerName: t('reports.location', { defaultValue: 'Location' }),
        flex: 1,
        minWidth: 200,
      },
      { field: 'comm', headerName: t('reports.commStatus', { defaultValue: 'Comm Status' }), width: 130 },
      { field: 'maintenance', headerName: t('reports.maintenance', { defaultValue: 'Maintenance' }), width: 140 },
    ],
    [t, isBillboard, relayLoadTerm, baseStructureLink],
  );

  const handleExport = useCallback(() => {
    exportReportToExcel({
      title: t('reports.disabledReport', { type: relayLoadTermPlural }),
      fileName: `disabled-${isBillboard ? 'panels' : 'zones'}-report`,
      rows,
      columns,
      siteGroup,
      isBillboard,
    });
  }, [rows, columns, siteGroup, isBillboard, relayLoadTermPlural]);

  const exportButton = useMemo(
    () => (
      <ToolbarIconButton
        tooltip={t('general.export', { defaultValue: 'Export' })}
        onClick={handleExport}
        disabled={loading || rows.length === 0}
      >
        {loading ? <CircularProgress size={18} /> : <FileDownload sx={{ fontSize: 18 }} />}
      </ToolbarIconButton>
    ),
    [t, handleExport, loading, rows.length],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <DataGridToolbar apiRef={apiRef} toolbarActions={exportButton} quickFilterPlaceholder={t('general.search')} />
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        loading={loading}
        density="compact"
        hideFooter
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: t('general.noResultsMessage') }}
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Box>
  );
}

export default DisabledPanelsReportGrid;
